import { sepAPI } from '../API';
import { setSnackbarForEndpoint } from '../../utils/snackbar';
import {
  UserNote,
  CreateUserNoteBody,
  UpdateUserNoteBody,
} from '../../../shared/types/UserNote';
import { SEPSearchResult } from '../../../shared/types/SEP';
import { sepSlice } from '../sepSlice';

// Special SEP ID for storing user templates (not bound to specific SEP)
const TEMPLATE_SEP_ID = 20000;

/**
 * Helper to update userNotesCount in all cached findSeps queries
 * @param api - The RTK Query API object from onQueryStarted
 * @param sepID - The SEP ID to update
 * @param delta - The change to apply (+1 for create, -1 for delete)
 */
const updateSepNotesCountInCache = (
  api: any,
  sepID: number,
  delta: number
) => {
  // Skip templates - they don't affect SEP note counts
  if (sepID === TEMPLATE_SEP_ID) return;

  // Get all cached queries for findSeps
  const state = api.getState();
  const queries = state.sepAPI?.queries || {};

  // Find all findSeps queries and update them
  Object.keys(queries).forEach((queryKey) => {
    if (queryKey.startsWith('findSeps(')) {
      const queryArgs = queries[queryKey]?.originalArgs;
      if (queryArgs) {
        api.dispatch(
          sepSlice.util.updateQueryData('findSeps', queryArgs, (draft: SEPSearchResult) => {
            const sep = draft.seps?.find((s) => s.id === sepID);
            if (sep) {
              sep.userNotesCount = Math.max(0, (sep.userNotesCount || 0) + delta);
            }
          })
        );
      }
    }
  });
};

/**
 * RTK Query slice for personal/private user notes.
 * Notes are only visible to their creator - no cross-user visibility.
 */
export const userNoteSlice = sepAPI.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get all personal notes for a SEP
     */
    getUserNotesBySep: builder.query<UserNote[], number>({
      query: (sepID) => `sep/${sepID}/notes`,
      providesTags: (result, error, sepID) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'UserNotes' as const, id })),
              { type: 'UserNotes', id: `SEP-${sepID}` },
            ]
          : [{ type: 'UserNotes', id: `SEP-${sepID}` }],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting your notes.',
        });
      },
    }),

    /**
     * Create a new personal note
     */
    createUserNote: builder.mutation<UserNote, CreateUserNoteBody>({
      query: (body) => ({
        url: 'note',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'UserNotes', id: `SEP-${arg.sepID}` },
      ],
      onQueryStarted: async (arg, api) => {
        const isTemplate = arg.template === 1;
        const label = isTemplate ? 'Template' : 'Note';
        const name = arg.title?.trim() || 'Untitled';
        const context = isTemplate ? '' : ` for SEP #${arg.sepID}`;
        setSnackbarForEndpoint(api, {
          successMessage: `${label} "${name}" created${context}!`,
          errorMessage: `There was a problem creating your ${label.toLowerCase()}.`,
        });

        // Update userNotesCount in SEP list cache after successful creation
        try {
          await api.queryFulfilled;
          // Only increment count for regular notes, not templates
          if (arg.template !== 1) {
            updateSepNotesCountInCache(api, arg.sepID, 1);
          }
        } catch {
          // Error handled by snackbar
        }
      },
    }),

    /**
     * Update an existing note
     */
    updateUserNote: builder.mutation<
      UserNote,
      { id: number; sepID: number } & UpdateUserNoteBody
    >({
      query: ({ id, sepID, ...body }) => ({
        url: `note/${id}`,
        method: 'PATCH',
        body: { sepID, ...body },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'UserNotes', id: arg.id },
        { type: 'UserNotes', id: `SEP-${arg.sepID}` },
      ],
      onQueryStarted: (arg, api) => {
        const isTemplate = arg.template === 1;
        const label = isTemplate ? 'Template' : 'Note';
        const name = arg.title?.trim() || 'Untitled';
        const context = isTemplate ? '' : ` for SEP #${arg.sepID}`;
        setSnackbarForEndpoint(api, {
          successMessage: `${label} "${name}" updated${context}!`,
          errorMessage: `There was a problem updating your ${label.toLowerCase()}.`,
        });
      },
    }),

    /**
     * Delete a note (soft delete)
     */
    deleteUserNote: builder.mutation<void, { id: number; sepID: number; template?: boolean; title?: string }>({
      query: ({ id }) => ({
        url: `note/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'UserNotes', id: arg.id },
        { type: 'UserNotes', id: `SEP-${arg.sepID}` },
      ],
      onQueryStarted: async (arg, api) => {
        const label = arg.template ? 'Template' : 'Note';
        const name = arg.title?.trim() || 'Untitled';
        const context = arg.template ? '' : ` from SEP #${arg.sepID}`;
        setSnackbarForEndpoint(api, {
          successMessage: `${label} "${name}" deleted${context}!`,
          errorMessage: `There was a problem deleting your ${label.toLowerCase()}.`,
        });

        // Update userNotesCount in SEP list cache after successful deletion
        try {
          await api.queryFulfilled;
          // Only decrement count for regular notes, not templates
          if (!arg.template) {
            updateSepNotesCountInCache(api, arg.sepID, -1);
          }
        } catch {
          // Error handled by snackbar
        }
      },
    }),

  }),
});

export const {
  useGetUserNotesBySepQuery,
  useCreateUserNoteMutation,
  useUpdateUserNoteMutation,
  useDeleteUserNoteMutation,
} = userNoteSlice;
